import { addPipelineSchema } from '../../../schema/pipeline_schema';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { editPipelineThunk } from '../../../redux/thunks/pipeline';
import { PipelineParams } from '../../../params/pipeline';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: UpdatePipelineModal
 *
 * @paragraph: A modal component for editing an existing pipeline's details.
 * Users can modify the pipeline name, default status, and deal default value.
 * The modal also provides an option to delete the pipeline.
 *
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 * @param: {function} onDelete - Function to handle deleting the pipeline.
 */

const UpdatePipelineModal = ({ open, onClose, onDelete }: { open: boolean; onClose: () => void; onDelete: () => void; }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values based on the selected pipeline.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders/selectedPipeline: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { loaders, selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    // Initial form values based on the selected pipeline
    const initialValues: PipelineParams = {
        name: selectedPipeline?.name as string || '',
        is_default: selectedPipeline?.is_default as 0 | 1,
        deal_default_value: selectedPipeline?.deal_default_value || 0,
        client_id: 0,
        template_id: 0
    };

    /**
     * @heading3: Functions
    * @heading4: Handles the form submission for updating the pipeline details.
    * @paragraph: Dispatches the editPipelineThunk to perform the update.
    * @param: {PipelineParams} values - The values entered in the form.
    */
    const handleSubmit = async (values: PipelineParams) => {
        try {
            await dispatch(editPipelineThunk({
                data: {
                    id: selectedPipeline?.id as number,
                    name: values.name,
                    is_default: values.is_default,
                    deal_default_value: values.deal_default_value as number
                },
                client_id: id ? parseInt(id as string, 10) : 0
            })).unwrap();
            onClose();
        } catch (e) {
            //
        }
    };

    return <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title="Edit Pipeline">
        <Formik
            initialValues={initialValues}
            validationSchema={addPipelineSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Input field for pipeline name */}
                        <CustomInput
                            value={values.name}
                            onChange={handleChange('name')}
                            onBlur={handleBlur('name')}
                            error={touched.name && errors.name}
                            fullWidth
                            label="Pipeline Name"
                            placeholder="Pipeline Name"
                            variant="outlined"
                            disabled={loaders.isUpdating}
                        />
                        {/* Input field for deal default value */}
                        <CustomInput
                            type="number"
                            value={values.deal_default_value}
                            onChange={handleChange('deal_default_value')}
                            onBlur={handleBlur('deal_default_value')}
                            error={touched.deal_default_value && errors.deal_default_value}
                            fullWidth
                            label={
                                <>
                                    Deal Default Value <Typography component="span" fontSize="0.85rem" color="text.secondary">(dollar amount per deal)</Typography>
                                </>
                            }
                            placeholder="Enter value"
                            variant="outlined"
                            disabled={loaders.isUpdating}
                        />
                        {/* Checkbox for setting pipeline as default */}
                        <CustomInput
                            type="checkbox"
                            label="Make this default pipeline"
                            checked={values.is_default === 1}
                            onChange={(e) => handleChange({ target: { name: 'is_default', value: e.target.checked ? 1 : 0 }})}
                            disabled={loaders.isUpdating}
                        />
                        {/* Action buttons */}
                        <Box display="flex" gap={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isUpdating}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={onDelete} fullWidth disabled={loaders.isUpdating}>
                                Delete Pipeline
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isUpdating}>
                                {loaders.isUpdating ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default UpdatePipelineModal;